import {ActionIcon, createStyles, Indicator, Popover, px, useMantineTheme} from '@mantine/core';
import {css} from 'aphrodite';
import classNames from 'classnames';
import React, {useCallback, useRef, useState} from 'react';

import {getTabStyles} from 'nav/navbar/NavBarStyles';
import {ITEM_SIZE_SIDE_NAVBAR} from 'styles/StyleConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import {getBadgeText, InAppNotificationStatus, NotificationTab} from './InAppNotificationConstants';
import NotificationPanel from './NotificationPanel';
import {getPanelOffset} from './notificationPanelPlacement';
import {
  addNotificationAction,
  getNotificationActionContext,
  NotificationRumAction,
} from './notificationRumActions';
import useInAppNotifications from './useInAppNotifications';

// The panel is much larger than a typical popover, so its caret is enlarged from the theme default
const PANEL_ARROW_SIZE = 16;
// Height of the unread-count badge; wider labels like "9+" stretch it horizontally into a pill
const UNREAD_BADGE_SIZE = 22;

const useStyles = createStyles((theme) => ({
  // The bell sits in the fixed-bottom stack of the nav rail above the user avatar
  bellWrapper: {
    width: ITEM_SIZE_SIDE_NAVBAR,
    height: ITEM_SIZE_SIDE_NAVBAR,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  // Hover/open background comes from the shared nav-rail tab styles (getTabStyles) applied to the
  // slot wrapper, matching the other rail icons. The nucleus `transparent` ActionIcon variant tints
  // the glyph grey on hover; override it (doubled specificity to beat the variant selector) so the
  // bell stays white and transparent in every state, letting only the rail highlight show through.
  bellButton: {
    color: theme.fn.rgba(theme.white, 0.92),
    backgroundColor: 'transparent',
    '&&:hover, &&:active, &&:focus': {
      color: theme.fn.rgba(theme.white, 0.92),
      backgroundColor: 'transparent',
    },
  },
  dropdown: {
    padding: 0,
  },
  // Mantine draws the caret as a rotated square straddling the dropdown edge and layers it above the
  // panel content, so its inner half shows as a white diamond tip over the gray list. Clip it to the
  // outer half (the bordered left/bottom sides of the square for a `right-*` popover) so only the
  // triangle outside the panel remains.
  arrow: {
    clipPath: 'polygon(0 0, 0 100%, 100% 100%)',
  },
  badge: {
    padding: `0 ${theme.spacing.sm}`,
    userSelect: 'none',
  },
}));

/**
 * The notification bell in the global left nav rail: an unread-count badge over a bell button.
 * Clicking either the bell or the badge toggles the notification panel. The badge polls the unread
 * summary continuously; the (heavier) listing query only runs while the panel is open.
 */
export default function NotificationBellIcon(): JSX.Element {
  const {classes} = useStyles();
  const theme = useMantineTheme();
  // Gap between the open panel and both the nav rail's right edge and the bottom of the page
  const panelGap = px(theme.spacing.md);
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<NotificationTab>(NotificationTab.ALL);
  const [panelOffset, setPanelOffset] = useState({mainAxis: panelGap, crossAxis: 0});
  // When the panel was opened, so closing it can report how long it stayed open.
  const openedAtRef = useRef<number | null>(null);

  const {
    unreadCount,
    notifications,
    isLoading,
    isError,
    hasMore,
    loadMore,
    hasNewNotifications,
    refreshHead,
    markRead,
    markAllRead,
    dismiss,
    refetch,
  } = useInAppNotifications(activeTab, {isPanelOpen: isOpen});

  // Surface any head-refresh rejection through the panel rather than as an unhandled rejection; the
  // pill's click drives this so the caught error just leaves the list as-is.
  const handleShowNew = useCallback(() => {
    addNotificationAction(NotificationRumAction.CLICKED_SHOW_NEW, {tab: activeTab});
    refreshHead().catch(() => {
      // Swallowed: a failed refresh leaves the accumulated list in place.
    });
  }, [activeTab, refreshHead]);

  // Every open and close goes through here so each is reported exactly once, whichever control
  // (bell, outside click, Escape, a link opened in place) caused it.
  const setPanelOpen = useCallback(
    (opened: boolean) => {
      if (opened && openedAtRef.current == null) {
        openedAtRef.current = Date.now();
        addNotificationAction(NotificationRumAction.OPENED_INBOX, {unreadCount});
      } else if (!opened && openedAtRef.current != null) {
        addNotificationAction(NotificationRumAction.CLOSED_INBOX, {
          openDurationMs: Date.now() - openedAtRef.current,
        });
        openedAtRef.current = null;
      }
      setIsOpen(opened);
    },
    [unreadCount]
  );

  const findNotification = useCallback(
    (id: string) => notifications.find((notification) => notification.id === id),
    [notifications]
  );

  // Following a body link marks its notification read. A link opened in place also closes the panel
  // so the destination isn't covered by it; one opened elsewhere (e.g. Cmd/Ctrl-click to a new tab)
  // leaves the panel open.
  const handleLinkClick = useCallback(
    (id: string, openedInPlace: boolean) => {
      addNotificationAction(NotificationRumAction.CLICKED_LINK, {
        ...getNotificationActionContext(findNotification(id)),
        openedInPlace,
      });
      markRead(id);
      if (openedInPlace) {
        setPanelOpen(false);
      }
    },
    [findNotification, markRead, setPanelOpen]
  );

  const handleDismiss = useCallback(
    (id: string) => {
      addNotificationAction(
        NotificationRumAction.DISMISSED,
        getNotificationActionContext(findNotification(id))
      );
      return dismiss(id);
    },
    [dismiss, findNotification]
  );

  const handleMarkAllRead = useCallback(() => {
    addNotificationAction(NotificationRumAction.MARKED_ALL_READ, {
      unreadCount,
      markedCount: notifications.filter(
        (notification) => notification.status === InAppNotificationStatus.UNREAD
      ).length,
    });
    return markAllRead();
  }, [markAllRead, notifications, unreadCount]);

  const handleTabChange = useCallback((tab: NotificationTab) => {
    addNotificationAction(NotificationRumAction.CHANGED_TAB, {tab});
    setActiveTab(tab);
  }, []);

  const handleLoadMore = useCallback(() => {
    addNotificationAction(NotificationRumAction.LOADED_MORE, {
      tab: activeTab,
      loadedCount: notifications.length,
    });
    loadMore();
  }, [activeTab, loadMore, notifications.length]);

  // The bell is pinned to the rail's bottom stack, so its distance to the page bottom, and therefore
  // the offset measured here, holds across resizes.
  const handleBellClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setPanelOffset(
        getPanelOffset(event.currentTarget.getBoundingClientRect(), window.innerHeight, panelGap)
      );
      setPanelOpen(!isOpen);
    },
    [isOpen, panelGap, setPanelOpen]
  );

  // Surface any refetch rejection through the panel's error state rather than as an unhandled
  // rejection; the hook already loads the listing on first open and refreshes on summary advance,
  // so this is only wired to the panel's explicit retry affordance.
  const handleRetry = useCallback(() => {
    refetch().catch(() => {
      // Swallowed: `isError` from the hook drives the panel's error UI.
    });
  }, [refetch]);

  const badgeText = getBadgeText(unreadCount);
  const bellLabel = unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications';

  return (
    <div className={classNames(css(getTabStyles(isOpen)), classes.bellWrapper)}>
      <Popover
        opened={isOpen}
        onChange={setPanelOpen}
        position="right-end"
        offset={panelOffset}
        arrowPosition="center"
        arrowSize={PANEL_ARROW_SIZE}
        shadow="xl"
        radius="lg"
        withinPortal={true}
        classNames={{dropdown: classes.dropdown, arrow: classes.arrow}}
      >
        <Tooltip isDisabled={isOpen} placement={FloatingPlacement.RIGHT} text="Notifications">
          <Popover.Target>
            {/* The badge renders inside the bell button so clicks on it activate the button.
                Popover.Target supplies aria-haspopup/aria-expanded/aria-controls to the button. */}
            <ActionIcon
              variant="transparent"
              size="lg"
              className={classes.bellButton}
              aria-label={bellLabel}
              onClick={handleBellClick}
              {...getTestProps({component: 'NotificationBellIcon', element: 'bell'})}
            >
              <Indicator
                label={badgeText ?? undefined}
                disabled={badgeText == null}
                color="red"
                size={UNREAD_BADGE_SIZE}
                classNames={{indicator: classes.badge}}
              >
                <BenchlingIcon icon="bell" />
              </Indicator>
            </ActionIcon>
          </Popover.Target>
        </Tooltip>
        <Popover.Dropdown>
          <NotificationPanel
            activeTab={activeTab}
            onTabChange={handleTabChange}
            notifications={notifications}
            unreadCount={unreadCount}
            isLoading={isLoading}
            isError={isError}
            hasMore={hasMore}
            onLoadMore={handleLoadMore}
            hasNewNotifications={hasNewNotifications}
            onShowNew={handleShowNew}
            onLinkClick={handleLinkClick}
            onMarkAllRead={handleMarkAllRead}
            onDismiss={handleDismiss}
            onRetry={handleRetry}
          />
        </Popover.Dropdown>
      </Popover>
    </div>
  );
}
